import { Form, Formik } from "formik";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Client } from "@/features/clients/types";
import { FormEditorActions } from "@/features/forms/components/form-editor-actions";
import type { FormSubmission } from "@/features/forms/types";
import { schema8857, toInitialValues8857 } from "./fields";
import { SrPart1 } from "./components/sr-part1";
import { SrPart2 } from "./components/sr-part2";
import { SrPart3 } from "./components/sr-part3";
import { SrPart4 } from "./components/sr-part4";
import { SrPart5 } from "./components/sr-part5";
import { SrPart6 } from "./components/sr-part6";
import { SrPart7 } from "./components/sr-part7";

type TabKey = "part1" | "part2" | "part3" | "part4" | "part5" | "part6" | "part7";
const PART_TO_TAB: Record<string, TabKey> = {
  "1": "part1", "2": "part2", "3": "part3", "4": "part4", "5": "part5", "6": "part6", "7": "part7",
};
const TAB_TO_PART: Record<TabKey, string> = {
  part1: "1", part2: "2", part3: "3", part4: "4", part5: "5", part6: "6", part7: "7",
};

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
  /** When launched from a Case, enables Part IV's shared asset list + records
   *  progress on a `ResolutionWorkflow`. Absent in the client-scoped / plain
   *  8857-editor variants (assets are case-nested — see the Part IV nudge). */
  caseId?: string;
}

/**
 * The Spouse-Relief wizard: a 7-tab stepper (Part I…VII) over the client's single
 * Form 8857 (Request for Innocent Spouse Relief) submission. One Formik form holds
 * the whole `data` blob so tab switches never lose data, and `FormEditorActions`
 * provides autosave + Print (preview) / Archive (save-to-Documents) + the e-sign
 * stub. The wizard and the plain 8857 editor are two views of the same record.
 * Honors a `?part=1…7` deep-link.
 */
export function SpouseReliefWizard({ client, submission, onSubmissionChange, caseId }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = PART_TO_TAB[searchParams.get("part") ?? "1"] ?? "part1";

  const goToTab = (next: TabKey) => {
    const params = new URLSearchParams(searchParams);
    params.set("part", TAB_TO_PART[next]);
    setSearchParams(params, { replace: true });
  };

  return (
    <Formik
      initialValues={toInitialValues8857(submission.data)}
      validationSchema={schema8857}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Tabs value={tab} onValueChange={(v) => goToTab(v as TabKey)}>
          <TabsList className="flex-wrap">
            <TabsTrigger value="part1">Part I</TabsTrigger>
            <TabsTrigger value="part2">Part II</TabsTrigger>
            <TabsTrigger value="part3">Part III</TabsTrigger>
            <TabsTrigger value="part4">Part IV</TabsTrigger>
            <TabsTrigger value="part5">Part V</TabsTrigger>
            <TabsTrigger value="part6">Part VI</TabsTrigger>
            <TabsTrigger value="part7">Part VII</TabsTrigger>
          </TabsList>
          <TabsContent value="part1" className="mt-6">
            <SrPart1 onNext={() => goToTab("part2")} />
          </TabsContent>
          <TabsContent value="part2" className="mt-6">
            <SrPart2 client={client} onNext={() => goToTab("part3")} />
          </TabsContent>
          <TabsContent value="part3" className="mt-6">
            <SrPart3 onNext={() => goToTab("part4")} />
          </TabsContent>
          <TabsContent value="part4" className="mt-6">
            <SrPart4 caseId={caseId} onNext={() => goToTab("part5")} />
          </TabsContent>
          <TabsContent value="part5" className="mt-6">
            <SrPart5 onNext={() => goToTab("part6")} />
          </TabsContent>
          <TabsContent value="part6" className="mt-6">
            <SrPart6 onNext={() => goToTab("part7")} />
          </TabsContent>
          <TabsContent value="part7" className="mt-6">
            <SrPart7 />
          </TabsContent>
        </Tabs>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
